import React, { useState, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { getServicesManager } from '../../services';

interface SalesforceIntegrationProps {
  apiKey: string;
}

interface SalesforceStatus {
  connected: boolean;
  instance_url?: string;
}

interface CredentialsStatus {
  configured: boolean;
  client_id?: string;
  is_sandbox?: boolean;
}

export function SalesforceIntegration({ apiKey }: SalesforceIntegrationProps) {
  const [status, setStatus] = useState<SalesforceStatus | null>(null);
  const [credentials, setCredentials] = useState<CredentialsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSandbox, setIsSandbox] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [testRecordId, setTestRecordId] = useState('');
  const [testWhatId, setTestWhatId] = useState('');
  const [testing, setTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);
  const [taskTypes, setTaskTypes] = useState<Array<{ label: string; value: string; active: boolean }>>([]);
  const [resultFields, setResultFields] = useState<Array<{ field_name: string; field_label: string; field_type: string; picklist_values?: Array<{ label: string; value: string }> }>>([]);
  const [selectedTaskType, setSelectedTaskType] = useState<string>('');
  const [fieldMappings, setFieldMappings] = useState<Record<string, { mode: string; value?: string }>>({});
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [mappingSuccess, setMappingSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'configured' | 'unconfigured'>('all');
  const [isMappingExpanded, setIsMappingExpanded] = useState(false);
  const [originalFieldMappings, setOriginalFieldMappings] = useState<Record<string, { mode: string; value?: string }>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const services = getServicesManager();

  // Filter and search fields
  const filteredFields = resultFields.filter(field => {
    // Search filter
    const matchesSearch = searchQuery === '' ||
      field.field_label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      field.field_name.toLowerCase().includes(searchQuery.toLowerCase());

    // Mode filter
    const mapping = fieldMappings[field.field_name];
    const isConfigured = mapping && mapping.mode !== 'none';

    if (filterMode === 'configured' && !isConfigured) return false;
    if (filterMode === 'unconfigured' && isConfigured) return false;

    return matchesSearch;
  });

  const configuredFieldsCount = Object.values(fieldMappings).filter(m => m.mode !== 'none').length;

  // Check if credentials are configured
  const checkCredentials = async () => {
    try {
      const data = await services.getSalesforceAppCredentials();
      if (data) {
        setCredentials(data);
        if (data.configured && data.client_id) {
          setClientId(data.client_id);
          // Load the value from backend if it exists, otherwise keep current state (defaults to false for production)
          if (data.is_sandbox !== undefined) {
            setIsSandbox(data.is_sandbox);
          }
        }
      } else {
        setCredentials({ configured: false });
      }
    } catch (error) {
      console.error('Error checking Salesforce credentials:', error);
      setCredentials({ configured: false });
    }
  };

  // Save credentials
  const handleSaveCredentials = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Please enter both Client ID and Client Secret');
      return;
    }

    try {
      setSavingCredentials(true);
      setError(null);

      const result = await services.postSalesforceAppCredentials(clientId.trim(), clientSecret.trim(), isSandbox);
      if (result?.success) {
        setCredentials({ configured: true, client_id: clientId.trim(), is_sandbox: isSandbox });
        setShowSetup(false);
        setClientSecret(''); // Clear secret after saving
        // Refresh status after saving credentials
        await checkStatus();
      } else {
        throw new Error('Failed to save credentials');
      }
    } catch (error) {
      console.error('Error saving Salesforce credentials:', error);
      setError(error instanceof Error ? error.message : 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  // Check Salesforce connection status
  const checkStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await services.getSalesforceOAuthStatus();
      if (data) {
        setStatus(data);
      } else {
        setStatus({ connected: false });
      }
    } catch (error) {
      console.error('Error checking Salesforce status:', error);
      setError(error instanceof Error ? error.message : 'Failed to check connection status');
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  // Initiate OAuth flow
  const handleConnect = async () => {
    try {
      setError(null);
      const redirectUri = `${window.location.origin}/settings/integrations/salesforce/callback`;

      const data = await services.getSalesforceOAuthAuthorize(redirectUri);
      if (data) {
        // Redirect to Salesforce authorization page
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error initiating Salesforce OAuth:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect to Salesforce');
    }
  };

  // Disconnect Salesforce
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Salesforce? Call notes will no longer be synced automatically.')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      const result = await services.postSalesforceOAuthDisconnect();
      if (result?.success) {
        setStatus({ connected: false });
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting Salesforce:', error);
      setError(error instanceof Error ? error.message : 'Failed to disconnect from Salesforce');
    } finally {
      setDisconnecting(false);
    }
  };

  // Test integration by writing a fake call log
  const handleTestIntegration = async () => {
    if (!testRecordId.trim() && !testWhatId.trim()) {
      setError('Please enter a Lead/Contact ID or Account/Opportunity ID');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestSuccess(null);

      const result = await services.testSalesforceIntegration(
        testRecordId.trim() || undefined,
        testWhatId.trim() || undefined
      );
      if (result?.success) {
        setTestSuccess(`Successfully created test call log${result.record_type ? ` for ${result.record_type} ${testRecordId}` : ''}`);
        setTestRecordId('');
        setTestWhatId('');
      } else {
        throw new Error(result?.error || 'Failed to create test call log');
      }
    } catch (error) {
      console.error('Error testing Salesforce integration:', error);
      setError(error instanceof Error ? error.message : 'Failed to test integration');
    } finally {
      setTesting(false);
    }
  };

  const loadTaskMappingOptions = async () => {
    setLoadingMappings(true);
    try {
      const configData = await services.getSalesforceConfig();

      if (configData?.success) {
        if (configData.task_types) {
          setTaskTypes(configData.task_types);
        }
        if (configData.result_fields) {
          setResultFields(configData.result_fields);
        }
        if (configData.field_mappings) {
          setFieldMappings(configData.field_mappings);
          setOriginalFieldMappings(configData.field_mappings);
          // Extract task type from field mappings if it exists
          const typeMapping = configData.field_mappings['Type'];
          if (typeMapping?.mode === 'default' && typeMapping.value) {
            setSelectedTaskType(typeMapping.value);
          }
        } else {
          setFieldMappings({});
          setOriginalFieldMappings({});
        }
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Error loading SFDC configuration:', err);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleSaveMapping = async () => {
    setSavingMappings(true);
    setMappingSuccess(null);
    try {
      const result = await services.postSalesforceConfig(fieldMappings);
      if (result?.success) {
        setMappingSuccess('Configuration saved successfully');
        setOriginalFieldMappings(fieldMappings);
        setHasUnsavedChanges(false);
        setTimeout(() => setMappingSuccess(null), 3000);
      } else {
        setError(result?.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('An error occurred while saving configuration');
    } finally {
      setSavingMappings(false);
    }
  };

  const handleFieldMappingChange = (fieldName: string, mode: string, value?: string) => {
    setFieldMappings(prev => {
      const newMappings = { ...prev };

      if (mode === 'none') {
        // Remove the mapping entirely if mode is none
        delete newMappings[fieldName];
      } else {
        newMappings[fieldName] = { mode, value };
      }

      // Keep selected task type in sync with Type field mapping
      if (fieldName === 'Type') {
        if (mode === 'default' && value) {
          setSelectedTaskType(value);
        } else {
          setSelectedTaskType('');
        }
      }

      return newMappings;
    });
    setHasUnsavedChanges(true);
  };

  // Check if there are unsaved changes by comparing current with original
  useEffect(() => {
    const hasChanges = JSON.stringify(fieldMappings) !== JSON.stringify(originalFieldMappings);
    setHasUnsavedChanges(hasChanges);
  }, [fieldMappings, originalFieldMappings]);

  useEffect(() => {
    if (apiKey) {
      checkCredentials();
      checkStatus();
    }
  }, [apiKey]);

  useEffect(() => {
    if (status?.connected) {
      loadTaskMappingOptions();
    }
  }, [status?.connected]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center gap-3">
          <svg className="animate-spin h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <Typography variant="mediumParagraph" color="#6B7280">
            Checking Salesforce connection...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex flex-col gap-1">
            <Typography variant="mediumParagraph" color="#DC2626" fontWeight={600}>
              Error
            </Typography>
            <Typography variant="smallParagraph" color="#7F1D1D">
              {error}
            </Typography>
          </div>
        </div>
      )}

      {/* Credentials Setup */}
      {!credentials?.configured && !showSetup && (
        <div className="flex items-start gap-3 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col gap-1">
              <Typography variant="mediumParagraph" color="#D97706" fontWeight={600}>
                Setup Required
              </Typography>
              <Typography variant="smallParagraph" color="#92400E">
                Before connecting to Salesforce, you need to configure your Salesforce Connected App credentials.
              </Typography>
            </div>
            <button
              onClick={() => setShowSetup(true)}
              className="self-start px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors duration-200"
            >
              <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={600}>
                Configure Credentials
              </Typography>
            </button>
          </div>
        </div>
      )}

      {/* Credentials Setup Form */}
      {showSetup && (
        <div className="flex flex-col gap-4 p-6 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <Typography variant="h4" color="#1F2937" fontWeight={600}>
              Configure Salesforce Connected App
            </Typography>
            {credentials?.configured && (
              <button
                onClick={() => {
                  setShowSetup(false);
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                Client ID (Consumer Key)
              </Typography>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Enter your Salesforce Connected App Client ID"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                Client Secret (Consumer Secret)
              </Typography>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Enter your Salesforce Connected App Client Secret"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                id="isSandbox"
                checked={isSandbox}
                onChange={(e) => setIsSandbox(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label htmlFor="isSandbox" className="flex flex-col gap-1 cursor-pointer">
                <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                  This is a Salesforce Sandbox instance
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280">
                  Check this box if you're connecting to a Salesforce Sandbox environment (not production)
                </Typography>
              </label>
            </div>

            <div className="flex flex-col gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
                  How to Configure Your Salesforce Connected App
                </Typography>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left side - Step by step instructions */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={600}>
                        1
                      </Typography>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={600}>
                        Go to Salesforce Setup → App Manager
                      </Typography>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={600}>
                        2
                      </Typography>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={600}>
                        Create a new Connected App or select an existing one
                      </Typography>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={600}>
                        3
                      </Typography>
                    </div>
                    <div className="flex-1 flex flex-col gap-3">
                      <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={600}>
                        Configure OAuth Settings:
                      </Typography>
                      <div className="flex flex-col gap-2 pl-2">
                        <Typography variant="smallParagraph" color="#1E3A8A">
                          • Enable OAuth Settings
                        </Typography>

                        <div className="flex flex-col gap-1.5">
                          <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={500}>
                            • Callback URL:
                          </Typography>
                          <div className="flex items-center gap-2 p-2 bg-white border border-blue-200 rounded">
                            <input
                              type="text"
                              value={`${window.location.origin}/settings/integrations/salesforce/callback`}
                              readOnly
                              className="flex-1 text-xs font-mono text-gray-700 bg-transparent border-none outline-none"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/settings/integrations/salesforce/callback`);
                              }}
                              className="flex-shrink-0 p-1.5 hover:bg-blue-100 rounded transition-colors"
                              title="Copy to clipboard"
                            >
                              <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={500}>
                            • Selected OAuth Scopes:
                          </Typography>
                          <div className="pl-3 flex flex-col gap-0.5">
                            <Typography variant="smallParagraph" color="#1E3A8A">
                              - Manage user data via APIs (api)
                            </Typography>
                            <Typography variant="smallParagraph" color="#1E3A8A">
                              - Perform requests at any time (refresh_token)
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={600}>
                        4
                      </Typography>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" color="#1E3A8A" fontWeight={600}>
                        Flow Enablement:
                      </Typography>
                      <Typography variant="smallParagraph" color="#1E3A8A">
                        • Enable "Authorization Code and Credentials Flow"
                      </Typography>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <Typography variant="smallParagraph" color="#059669" fontWeight={600}>
                        Copy the Consumer Key (Client ID) and Consumer Secret (Client Secret) and paste them above
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* Right side - Reference image */}
                <div className="flex flex-col gap-2">
                  <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
                    Configuration Reference:
                  </Typography>
                  <div className="bg-white border border-blue-300 rounded-lg overflow-hidden">
                    <img
                      src="/assets/salesforce-config.png"
                      alt="Salesforce Connected App OAuth configuration settings"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  <Typography variant="smallParagraph" color="#64748B" className="italic text-center">
                    Example of OAuth settings in Salesforce Connected App
                  </Typography>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveCredentials}
                disabled={savingCredentials || !clientId.trim() || !clientSecret.trim()}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                {savingCredentials ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={600}>
                      Saving...
                    </Typography>
                  </>
                ) : (
                  <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={600}>
                    Save Credentials
                  </Typography>
                )}
              </button>
              {credentials?.configured && (
                <button
                  onClick={() => {
                    setShowSetup(false);
                    setError(null);
                  }}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors duration-200"
                >
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={600}>
                    Cancel
                  </Typography>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Configured Credentials Display */}
      {credentials?.configured && !showSetup && (
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <Typography variant="smallParagraph" color="#6B7280" fontWeight={600}>
                  Configured Client ID:
                </Typography>
                <Typography variant="smallParagraph" color="#374151" className="font-mono">
                  {credentials.client_id}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="#6B7280" fontWeight={600}>
                  Instance Type:
                </Typography>
                <span className={`px-2 py-0.5 rounded ${credentials.is_sandbox ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                  <Typography variant="smallParagraph" color={credentials.is_sandbox ? '#854D0E' : '#1E40AF'} className="text-xs" fontWeight={500}>
                    {credentials.is_sandbox ? 'Sandbox' : 'Production'}
                  </Typography>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSetup(true)}
                className="px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                  Edit
                </Typography>
              </button>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to remove this configuration? You will need to reconfigure credentials to connect again.')) {
                    try {
                      const result = await services.deleteSalesforceAppCredentials();
                      if (result?.success) {
                        setCredentials({ configured: false });
                        setClientId('');
                        setClientSecret('');
                        setIsSandbox(false);
                      } else {
                        throw new Error('Failed to delete credentials');
                      }
                    } catch (error) {
                      setError('Failed to remove configuration');
                    }
                  }
                }}
                className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                  Remove
                </Typography>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`
        flex items-center justify-between p-6 rounded-lg border-l-4
        ${status?.connected
          ? 'bg-emerald-50 border-emerald-500'
          : 'bg-gray-50 border-gray-300'
        }
      `}>
        <div className="flex items-center gap-4">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${status?.connected ? 'bg-emerald-100' : 'bg-gray-200'}
          `}>
            {status?.connected ? (
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Typography
              variant="mediumParagraph"
              color={status?.connected ? "#059669" : "#6B7280"}
              fontWeight={600}
            >
              {status?.connected ? 'Connected' : 'Not Connected'}
            </Typography>
            {status?.connected && status.instance_url && (
              <Typography variant="smallParagraph" color="#047857">
                {status.instance_url}
              </Typography>
            )}
            {!status?.connected && (
              <Typography variant="smallParagraph" color="#9CA3AF">
                Connect your Salesforce account to sync call notes automatically
              </Typography>
            )}
          </div>
        </div>

        {status?.connected ? (
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            {disconnecting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                  Disconnecting...
                </Typography>
              </>
            ) : (
              <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                Disconnect
              </Typography>
            )}
          </button>
        ) : (
          <button
            onClick={handleConnect}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={600}>
              Connect to Salesforce
            </Typography>
          </button>
        )}
      </div>

      {/* Task Mapping Configuration Section */}
      {status?.connected && (
        <div className="flex flex-col gap-4 p-6 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-purple-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            <div className="flex-1 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
                      Task Mapping Configuration
                    </Typography>
                    {!isMappingExpanded && configuredFieldsCount > 0 && (
                      <span className="px-2 py-0.5 bg-purple-100 rounded-full">
                        <Typography variant="smallParagraph" color="#7C3AED" className="text-xs" fontWeight={600}>
                          {configuredFieldsCount} configured
                        </Typography>
                      </span>
                    )}
                  </div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Customize how call notes are written to Salesforce Tasks
                  </Typography>
                </div>
                <button
                  onClick={() => {
                    setIsMappingExpanded(!isMappingExpanded);
                    if (!isMappingExpanded && resultFields.length === 0) {
                      loadTaskMappingOptions();
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  <Typography variant="smallParagraph" color="inherit" fontWeight={500}>
                    {isMappingExpanded ? 'Collapse' : 'Expand'}
                  </Typography>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${isMappingExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {isMappingExpanded && (
                <>
                  {mappingSuccess && (
                    <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <Typography variant="smallParagraph" color="#059669">
                        {mappingSuccess}
                      </Typography>
                    </div>
                  )}

                  {loadingMappings ? (
                    <div className="flex items-center gap-2 p-4">
                      <svg className="animate-spin h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <Typography variant="smallParagraph" color="#6B7280">
                        Loading configuration options...
                      </Typography>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {/* Header */}
                      <div className="flex flex-col gap-2">
                        <Typography variant="h3" color="#111827">
                          Task Field Configuration
                        </Typography>
                        <Typography variant="smallParagraph" color="#6B7280">
                          Configure how call data is written to Salesforce Task fields. For each field, you can choose to write a default value, use our call disposition, or skip the field entirely.
                        </Typography>
                      </div>

                      {/* Summary Stats */}
                      <div className="flex items-center gap-6 p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-center gap-2">
                          <Typography variant="smallParagraph" color="#6B7280">
                            Total Fields:
                          </Typography>
                          <Typography variant="smallParagraph" color="#111827" fontWeight={600}>
                            {resultFields.length}
                          </Typography>
                        </div>
                        <div className="flex items-center gap-2">
                          <Typography variant="smallParagraph" color="#6B7280">
                            Configured:
                          </Typography>
                          <Typography variant="smallParagraph" color="#7C3AED" fontWeight={600}>
                            {configuredFieldsCount}
                          </Typography>
                        </div>
                      </div>

                      {/* Search and Filter */}
                      <div className="flex gap-3">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            placeholder="Search fields..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ fontSize: '14px', fontFamily: 'Inter, sans-serif' }}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <select
                          value={filterMode}
                          onChange={(e) => setFilterMode(e.target.value as 'all' | 'configured' | 'unconfigured')}
                          style={{ fontSize: '14px', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        >
                          <option value="all">All Fields</option>
                          <option value="configured">Configured Only</option>
                          <option value="unconfigured">Unconfigured Only</option>
                        </select>
                      </div>

                      {/* Field List */}
                      <div className="flex flex-col gap-3">
                        {filteredFields.length === 0 ? (
                          <div className="p-8 text-center border border-gray-200 rounded-lg">
                            <Typography variant="smallParagraph" color="#6B7280">
                              {searchQuery || filterMode !== 'all' ? 'No fields match your search criteria' : 'No fields available'}
                            </Typography>
                          </div>
                        ) : (
                          filteredFields.map((field) => {
                            const mapping = fieldMappings[field.field_name] || { mode: 'none' };
                            const isConfigured = mapping.mode !== 'none';

                            return (
                              <div
                            key={field.field_name}
                            className={`p-4 rounded-lg flex flex-col gap-2 transition-colors ${
                              isConfigured
                                ? 'bg-purple-50 border-2 border-purple-200'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <Typography variant="smallParagraph" color="#1F2937" fontWeight={600}>
                                  {field.field_label}
                                </Typography>
                                <Typography variant="smallParagraph" color="#6B7280" className="text-xs">
                                  {field.field_name} • {field.field_type}
                                </Typography>
                              </div>
                              {isConfigured && (
                                <span className="px-2 py-1 bg-purple-600 rounded-full">
                                  <Typography variant="smallParagraph" color="#FFFFFF" className="text-xs">
                                    Configured
                                  </Typography>
                                </span>
                              )}
                            </div>

                            <div className="flex gap-2">
                              <select
                                value={mapping.mode}
                                onChange={(e) => handleFieldMappingChange(field.field_name, e.target.value, e.target.value === 'default' && field.picklist_values?.[0]?.value ? field.picklist_values[0].value : undefined)}
                                style={{ fontSize: '14px', fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                              >
                                <option value="none">Don't write to this field (system default)</option>
                                <option value="disposition">Use our call disposition</option>
                                <option value="default">Always use default value</option>
                              </select>

                              {mapping.mode === 'default' && field.picklist_values && (
                                <select
                                  value={mapping.value || ''}
                                  onChange={(e) => handleFieldMappingChange(field.field_name, 'default', e.target.value)}
                                  style={{ fontSize: '14px', fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                                >
                                  <option value="">Select value...</option>
                                  {field.picklist_values.map((pv) => (
                                    <option key={pv.value} value={pv.value}>
                                      {pv.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>

                            {mapping.mode === 'disposition' && (
                              <div className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                                <svg className="h-4 w-4 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <Typography variant="smallParagraph" color="#1E40AF" className="text-xs">
                                  Will write the call disposition value to this field
                                </Typography>
                              </div>
                            )}
                            {mapping.mode === 'default' && mapping.value && (
                              <div className="flex items-start gap-2 p-2 bg-green-50 rounded">
                                <svg className="h-4 w-4 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <Typography variant="smallParagraph" color="#065F46" className="text-xs">
                                  Will always write: <span className="font-semibold">{mapping.value}</span>
                                </Typography>
                              </div>
                            )}
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleSaveMapping}
                          disabled={savingMappings || !hasUnsavedChanges}
                          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
                        >
                          {savingMappings ? (
                            <>
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                                Saving...
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                              {hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
                            </Typography>
                          )}
                        </button>
                        {hasUnsavedChanges && !savingMappings && (
                          <Typography variant="smallParagraph" color="#F59E0B" className="text-sm">
                            You have unsaved changes
                          </Typography>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Test Integration Section */}
      {status?.connected && (
        <div className="flex flex-col gap-4 p-6 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <div className="flex-1 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
                  Test Integration
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280">
                  Verify your Salesforce integration by creating a test call log on a Lead or Contact record.
                </Typography>
              </div>

              {testSuccess && (
                <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <Typography variant="smallParagraph" color="#059669">
                    {testSuccess}
                  </Typography>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                    Lead or Contact ID (optional)
                  </Typography>
                  <input
                    type="text"
                    value={testRecordId}
                    onChange={(e) => setTestRecordId(e.target.value)}
                    placeholder="e.g., 00Q5e00000DvXXX or 0035e00000DvXXX"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && (testRecordId.trim() || testWhatId.trim())) {
                        handleTestIntegration();
                      }
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                    Account or Opportunity ID (optional)
                  </Typography>
                  <input
                    type="text"
                    value={testWhatId}
                    onChange={(e) => setTestWhatId(e.target.value)}
                    placeholder="e.g., 0015e00000DvXXX or 0065e00000DvXXX"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && (testRecordId.trim() || testWhatId.trim())) {
                        handleTestIntegration();
                      }
                    }}
                  />
                </div>

                <button
                  onClick={handleTestIntegration}
                  disabled={testing || (!testRecordId.trim() && !testWhatId.trim())}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
                >
                    {testing ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                          Testing...
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                        Test
                      </Typography>
                    )}
                </button>

                <Typography variant="smallParagraph" color="#9CA3AF" className="text-xs">
                  Tip: Find IDs in Salesforce by opening the record and copying from the URL. Leads start with 00Q, Contacts with 003, Accounts with 001, and Opportunities with 006.
                  <br />
                  Note: If you provide both a Lead and an Account/Opportunity, only the Lead will be linked due to Salesforce limitations.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
